import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Instagram, Facebook } from "lucide-react";
import { Star } from "./primitives";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 w-full z-50 transition-colors duration-500 ${
        scrolled ? "bg-[#0D1220]/85 backdrop-blur-xl border-b hairline" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <Star size={14} className="text-gold transition-transform duration-500 group-hover:rotate-90" />
          <span className="font-serif text-2xl tracking-[0.25em] font-[400]">YAHTOV</span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.label.toLowerCase()}-link`}
              className={({ isActive }) =>
                `label link-underline transition-colors duration-300 ${
                  isActive ? "text-gold" : "text-white/70 hover:text-white"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden flex flex-col gap-1.5 p-2"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          <span className={`w-6 h-px bg-white transition-transform duration-300 ${open ? "translate-y-2 rotate-45" : ""}`} />
          <span className={`w-6 h-px bg-white transition-opacity duration-300 ${open ? "opacity-0" : ""}`} />
          <span className={`w-6 h-px bg-white transition-transform duration-300 ${open ? "-translate-y-2 -rotate-45" : ""}`} />
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden bg-[#0D1220]/95 backdrop-blur-xl border-t hairline">
          <nav className="flex flex-col px-6 py-6 gap-5">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) => `label ${isActive ? "text-gold" : "text-white/80"}`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export const Footer = () => (
  <footer data-testid="footer" className="border-t hairline bg-[#0D1220] relative z-10">
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
      <div className="grid md:grid-cols-3 gap-12">
        <div>
          <div className="flex items-center gap-3 mb-6">
            <Star size={14} className="text-gold" />
            <span className="font-serif text-2xl tracking-[0.25em]">YAHTOV</span>
          </div>
          <p className="font-serif text-2xl font-[300] text-white/80 italic">Good work. God's way.</p>
          <p className="text-white/40 text-sm mt-4 max-w-xs leading-relaxed">
            A faith-driven creative agency serving businesses in Charlotte, NC.
          </p>
        </div>

        <div>
          <p className="label text-white/40 mb-6">Navigate</p>
          <ul className="space-y-3">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link to={n.to} className="text-white/70 hover:text-gold transition-colors duration-300 font-serif text-xl">
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="label text-white/40 mb-6">Connect</p>
          <a href="mailto:gregory@yahtov.com" className="block text-white/70 hover:text-gold transition-colors duration-300 font-serif text-xl mb-3">
            gregory@yahtov.com
          </a>
          <p className="text-white/50 mb-6">Charlotte, North Carolina</p>
          <div className="flex gap-4">
            <a href="#" aria-label="Instagram" data-testid="footer-instagram" className="text-white/50 hover:text-gold transition-colors duration-300">
              <Instagram strokeWidth={1.25} className="w-5 h-5" />
            </a>
            <a href="#" aria-label="Facebook" data-testid="footer-facebook" className="text-white/50 hover:text-gold transition-colors duration-300">
              <Facebook strokeWidth={1.25} className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>

      <div className="mt-16 pt-8 border-t hairline flex flex-col md:flex-row justify-between gap-4 items-center">
        <p className="text-white/30 text-xs tracking-wider">&copy; {new Date().getFullYear()} Yahtov. All rights reserved.</p>
        <p className="text-white/30 text-xs tracking-wider flex items-center gap-2">
          <Star size={9} className="text-gold" /> "God is Good" &mdash; Hebrew
        </p>
      </div>
    </div>
  </footer>
);
